import {
  Button,
  DatePicker,
  DateRange,
  IconWarningAlt,
  MomentObj,
  Select,
} from "@veneer/core";
import { useCallback, useEffect, useState } from "react";
import "../../assets/css/views/reports/reports.css";
import moment from "moment";
import LoaderModal from "../../components/Loader/loaderModal";
import ReportsLogic from "./ReportsLogic";
import { useTranslation } from "react-i18next";

// Define the type for each item in the select options
interface ISelectOption {
  value: number;
  label: string;
}

// Define the type for the date range
export interface IDateRange {
  startDate: string | moment.Moment | undefined;
  endDate: string | moment.Moment | undefined;
}

interface IDataReport {
  sp: string;
  value: string;
}

/**
 * Reports view component.
 */
export default function Reports() {
  const [value, setValue] = useState<number[]>([]);
  const [reportSelected, setReportSelected] = useState<IDataReport>({
    value: "",
    sp: "",
  });
  const [isWeekDate, setIsWeekDate] = useState<boolean>(false);
  const [isMonthDate, setIsMonthDate] = useState<boolean>(false);
  const [selectedYearMonthlyReport, setSelectedYearMonthlyReport] =
    useState<ISelectOption>();
  const [selectedMonthMonthlyReport, setSelectedMonthMonthlyReport] =
    useState<ISelectOption>();
  const [optionsYears, setOptionsYears] = useState<ISelectOption[]>();
  const [optionsMonths, setOptionsMonths] = useState<ISelectOption[]>();
  const [dateRange, setDateRange] = useState<IDateRange>({
    startDate: undefined,
    endDate: undefined,
  });
  const [t] = useTranslation("global");
  const {
    getReportList,
    getReport,
    dataReportList,
    dataReportListFiltered,
    loading,
    reportmessage,
    handleChangeSearchReport,
    calculateMonthsAvailablesToSelect,
    calculateYearsAvailablesToSelect,
  } = ReportsLogic();

  useEffect(() => {
    getReportList();
  }, [t]);

  /**
   * Handles the change event of the select component.
   * @param option - The selected option.
   */
  const onChange = (option: any) => {
    setValue([option.value]);
    setReportSelected({
      value: option?.label,
      sp: option?.sp,
    });
  };

  /**
   * Clears the selected value of the select component.
   */
  const onClear = () => {
    setValue([]);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
    setIsWeekDate(false);
    setIsMonthDate(false);
    setSelectedMonthMonthlyReport(undefined);
    setSelectedYearMonthlyReport(undefined);
    setReportSelected({ value: "", sp: "" });
  };

  /**
   * Handles the date change event of the date picker component.
   * @param key - The key of the date range property to update.
   * @param value - The new value of the date range property.
   */

  const handleOnDateChange = (
    key: keyof IDateRange,
    value: MomentObj | DateRange | null | undefined
  ) => {
    setDateRange((prev) => {
      let newValue: any;
      if (value && "isBefore" in value) {
        newValue = value;
      } else if (value && "startDate" in value && "endDate" in value) {
        newValue = key === "endDate" ? value : value;
      } else {
        newValue = undefined;
      }

      if (key === "startDate") {
        return {
          startDate: newValue,
          endDate: undefined,
        };
      }

      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const isStartDateOutsideRangeEditMass = (day: MomentObj) => {
    if (reportSelected.sp === "GetSheduledAgendasReport") {
      return false;
    }

    const endDate = moment.isMoment(dateRange.endDate)
      ? dateRange.endDate
      : moment(dateRange.endDate);

    if (!endDate.isValid()) {
      return day.isBefore(moment(), "day");
    }
    return day.isAfter(endDate, "day") || day.isAfter(moment(), "day");
  };

  const isEndDateOutsideRangeEditMass = (day: MomentObj) => {
    if (reportSelected.sp === "GetSheduledAgendasReport") {
      return false;
    }

    const startDate = moment.isMoment(dateRange.startDate)
      ? dateRange.startDate
      : moment(dateRange.startDate);

    if (!startDate.isValid()) {
      return day.isBefore(moment(), "day");
    }
    const endDateLimitBefore = startDate.clone().add(3, "months");
    const validationEndDateLimitBefore =
      day.isBefore(startDate, "day") || day.isAfter(endDateLimitBefore, "day");
    return validationEndDateLimitBefore || day.isAfter(moment(), "day");
  };

  const isDisabledButtonSearch = useCallback(() => {
    if (
      (reportSelected?.sp !== "" && dateRange.endDate && dateRange.startDate) ||
      isReportWithoutDates(reportSelected) ||
      (isWeekDate && dateRange.startDate) ||
      (isMonthDate && dateRange.startDate)
    ) {
      return false;
    } else {
      return true;
    }
  }, [
    reportSelected,
    dateRange.startDate,
    dateRange.endDate,
    isWeekDate,
    isMonthDate,
  ]);

  const isDisabledDates = useCallback(() => {
    return reportSelected?.sp === "" || isReportWithoutDates(reportSelected);
  }, [reportSelected]);

  const isReportWithoutDates = (report: IDataReport) => {
    return (
      report.sp === "GetPointsOfSaleReport" || report.sp === "GetUsersReport"
    );
  };

  const isWeekDateOutsideRange = (day: MomentObj | undefined) => {
    day = day ?? moment();
    const currentDate = moment();
    const startOfCurrentWeek = currentDate.startOf("week");
    const isBeforeCurrentWeek = day.isBefore(startOfCurrentWeek);
    const mondayIndex = 1;
    const mondayOfTheWeek = day.clone().weekday(mondayIndex);

    return !day.isSame(mondayOfTheWeek) || !isBeforeCurrentWeek;
  };

  useEffect(() => {
    setIsWeekDate(reportSelected.sp === "GetWeeklyCapturesReport");
  }, [reportSelected]);

  useEffect(() => {
    setIsMonthDate(reportSelected.sp === "GetMonthlyCapturesReport");
  }, [reportSelected]);

  useEffect(() => {
    setOptionsYears(calculateYearsAvailablesToSelect());
  }, [calculateYearsAvailablesToSelect]);

  useEffect(() => {
    setOptionsMonths(
      calculateMonthsAvailablesToSelect(selectedYearMonthlyReport?.value)
    );
  }, [selectedYearMonthlyReport, calculateMonthsAvailablesToSelect]);

  useEffect(() => {
    setSelectedMonthMonthlyReport(undefined);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
  }, [selectedYearMonthlyReport]);

  return (
    <div className="reports">
      {loading && (
        <div>
          <LoaderModal>
            <div>
              {" "}
              {t("header.generating")} {reportSelected.value}
            </div>
          </LoaderModal>
        </div>
      )}
      <h4 className="title-header">{t("header.reports")}</h4>
      <div className="report-header">
        <div className="form-item">
          {/* Select component */}
          <Select
            options={dataReportListFiltered}
            id="select-usage"
            label={t("modal.SelectOption")}
            placement="bottom"
            helperTextVisibility="auto"
            onChange={onChange}
            onClear={onClear}
            value={value}
            disabled={dataReportList.length > 0 ? false : true}
            showSearch={true}
            onSearch={handleChangeSearchReport}
          />
        </div>
        {!isWeekDate && !isMonthDate && (
          <>
            <div className="form-item">
              {/* Start date picker */}
              <DatePicker
                calendarOrientation={["right", "down"]}
                label={t("header.stardDate")}
                onChange={(date) => handleOnDateChange("startDate", date)}
                orientation="horizontal"
                placeholder="MM/DD/YYYY"
                value={
                  dateRange.startDate
                    ? dateRange.startDate.toString()
                    : undefined
                }
                disabled={isDisabledDates()}
                isOutsideRange={isStartDateOutsideRangeEditMass}
              />
            </div>
            <div className="form-item">
              {/* End date picker */}
              <DatePicker
                calendarOrientation={["right", "down"]}
                label={t("header.endDate")}
                onChange={(date) => handleOnDateChange("endDate", date)}
                orientation="horizontal"
                placeholder="MM/DD/YYYY"
                value={
                  dateRange.endDate ? dateRange.endDate.toString() : undefined
                }
                disabled={isDisabledDates()}
                isOutsideRange={isEndDateOutsideRangeEditMass}
              />
            </div>
          </>
        )}
        {isWeekDate && (
          <div className="form-item">
            {/* week date picker */}
            <DatePicker
              calendarOrientation={["right", "down"]}
              label={t("header.date")}
              onChange={(date) =>
                (!isWeekDateOutsideRange(date as MomentObj) || !date) &&
                handleOnDateChange("startDate", date)
              }
              orientation="horizontal"
              placeholder="MM/DD/YYYY"
              value={
                dateRange.startDate ? dateRange.startDate.toString() : undefined
              }
              enableOutsideDays
              monthYearNavigation
              isOutsideRange={isWeekDateOutsideRange}
              maxDate={moment().weekday(-7)} // get the last monday
            />
          </div>
        )}
        {isMonthDate && (
          <>
            <div className="form-item">
              <Select
                options={optionsYears ?? []}
                id="select-year-monthly-report"
                label={t("header.year")}
                placement="bottom"
                helperTextVisibility="auto"
                onChange={(option: any) =>
                  setSelectedYearMonthlyReport({
                    label: option?.label,
                    value: option?.value,
                  })
                }
                clearIcon={false}
                value={
                  selectedYearMonthlyReport && [selectedYearMonthlyReport.value]
                }
              />
            </div>
            <div className="form-item">
              <Select
                options={optionsMonths ?? []}
                id="select-month-monthly-report"
                label={t("header.month")}
                placement="bottom"
                helperTextVisibility="auto"
                onChange={(option: any) => {
                  option &&
                    setSelectedMonthMonthlyReport({
                      label: option?.label,
                      value: option?.value,
                    });
                  option &&
                    handleOnDateChange(
                      "startDate",
                      moment(
                        new Date(
                          selectedYearMonthlyReport?.value ?? 2024,
                          option.value
                        )
                      )
                    );
                }}
                clearIcon={false}
                value={
                  selectedMonthMonthlyReport
                    ? [selectedMonthMonthlyReport.value]
                    : []
                }
                disabled={
                  !selectedYearMonthlyReport ||
                  selectedYearMonthlyReport.value === 0
                }
              />
            </div>
          </>
        )}

        <div className="form-item">
          {/* Search button */}
          <Button
            onClick={() => getReport(reportSelected, dateRange)}
            disabled={isDisabledButtonSearch()}
          >
            {t("header.search")}
          </Button>
        </div>
      </div>
      {dataReportList.length === 0 && !loading && (
        <div className="alert-container">
          <div>
            <IconWarningAlt style={{ width: "80px", height: "80px" }} />
          </div>
          <p>{t("header.noItemsFound")}</p>
        </div>
      )}
      {reportmessage !== "" && !loading && (
        <div className="alert-container">
          <div>
            <IconWarningAlt
              style={{
                width: "80px",
                height: "80px",
                color:
                  reportmessage === "donwload_failed" ? "#D9534F" : "#0096D6",
              }}
            />
          </div>
          <p>{t(`header.${reportmessage}`) + " " + reportSelected.value}</p>
        </div>
      )}
    </div>
  );
}
